import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UiStore {
  // Navigation state
  sidebarOpen: boolean;

  // Loading states (for local UI, not API)
  isPageLoading: boolean;

  // Modal states
  activeModal: string | null;

  // Legend state
  legendOpen: boolean;

  // Sheet state
  isSheetOpen: boolean;

  // Mobile responsive
  isMobile: boolean;

  // Touch state
  isTouch: boolean;

  // Actions
  actions: {
    setSidebarOpen: (open: boolean) => void;
    setPageLoading: (loading: boolean) => void;
    setActiveModal: (modal: string | null) => void;
    setLegendOpen: (open: boolean) => void;
    setSheetOpen: (open: boolean) => void;
    setIsMobile: (mobile: boolean) => void;
    setIsTouch: (touch: boolean) => void;
  };
}

const useUiStore = create<UiStore>()(
  persist(
    (set) => ({
      // Initial state
      sidebarOpen: false,
      isPageLoading: false,
      activeModal: null,
      legendOpen: true, // Default open on desktop
      isSheetOpen: false,
      isMobile: false,
      isTouch: false,

      actions: {
        setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),
        setPageLoading: (loading: boolean) => set({ isPageLoading: loading }),
        setActiveModal: (modal: string | null) => set({ activeModal: modal }),
        setLegendOpen: (open: boolean) => set({ legendOpen: open }),
        setSheetOpen: (open: boolean) => set({ isSheetOpen: open }),
        setIsMobile: (mobile: boolean) => set({ isMobile: mobile }),
        setIsTouch: (touch: boolean) => set({ isTouch: touch }),
      },
    }),
    {
      name: 'coffee-app-ui',
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        legendOpen: state.legendOpen,
        // Don't persist loading states or modals
      }),
    },
  ),
);

// Actions
export const useUiStoreActions = () => useUiStore((state) => state.actions);

// Selectors (individual for better performance)
export const useUiSidebarOpen = () => useUiStore((state) => state.sidebarOpen);
export const useUiPageLoading = () => useUiStore((state) => state.isPageLoading);
export const useUiActiveModal = () => useUiStore((state) => state.activeModal);
export const useUiLegendOpen = () => useUiStore((state) => state.legendOpen);
export const useUiIsSheetOpen = () => useUiStore((state) => state.isSheetOpen);
export const useUiIsMobile = () => useUiStore((state) => state.isMobile);
export const useUiIsTouch = () => useUiStore((state) => state.isTouch);
